import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  Skeleton,
  Button,
  FormControl,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Fab,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PlayerRow from "../components/PlayerRow";
import HeroEmptyState from "../components/HeroEmptyState";
import { FilterBar } from "../components/FilterBar";
import { DataTableShell, type ColumnDef, type SortState } from "../components/DataTableShell";
import { MobileCard } from "../components/MobileCard";
import { ColumnToggle } from "../components/ColumnToggle";
import { PositionChip, EmptyState, PlatformChip, PlatformSplitBar } from "../components/ui";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import {
  computePlayerCLVMulti,
  dominantProvider,
  groupDataByPlayer,
  buildPlayersByDraftIndex,
  normalizePlayerName,
} from "../helpers/playerHelpers";
import {
  draftTotalsByProvider,
  playerPlatformStats,
  sortPlatforms,
  type PlayerPlatformStat,
} from "../helpers/platformSplit";
import { getPlatformColor, getPlatformShortLabel } from "../config/theme";
import {
  PLAYER_TABLE_COLUMNS,
  DEFAULT_ADP_RANGE,
  TEAM_COLORS,
  POSITION_COLORS,
  PAGE_SIZE,
} from "../constants";
import { useSearchParams } from "react-router-dom";
import { useDraftData } from "../context/DraftDataContext";
import { useAdp } from "../context/AdpContext";
import { useMarketAdp } from "../helpers/useMarketAdp";
import { buildMarketSeriesByKey } from "../helpers/marketSeries";
import { useUIState } from "../context/UIStateContext";
import { brand } from "../config/theme";
import { useStatusColors } from "../helpers/statusColors";
import type { SortColumn, SortDirection, CsvRow } from "../types/draft";

const POSITIONS = ["QB", "RB", "WR", "TE"];

/** Metrics whose value depends on which platform's board you score against, and
 *  which therefore split into one sub-column per platform in a mixed portfolio. */
const SPLIT_METRICS = new Set(["exposure", "myAdp", "udAdp", "clv", "clvPct"]);
const MOBILE_SORT_OPTIONS: Array<{ value: SortColumn; label: string }> = PLAYER_TABLE_COLUMNS.map(col => ({
  value: col.id as SortColumn,
  label: col.label,
}));

// ── Mobile player card header / summary ───────────────────────────

interface MobilePlayerHeaderProps {
  name: string;
  team: string;
  position: string;
  exposure: string;
  count: number;
  posColor: string;
  /** Per-platform exposure. When present, replaces the single blended bar —
   *  the blended figure divides by every draft on both platforms and so
   *  understates a player concentrated on the smaller one. */
  platformStats?: PlayerPlatformStat[];
}

function MobilePlayerHeader({ name, team, position, exposure, count, posColor, platformStats }: MobilePlayerHeaderProps) {
  const expNum = parseFloat(exposure);
  const expPct = isNaN(expNum) ? 0 : Math.min(expNum, 100);
  return (
    <>
      <PositionChip position={position} size="sm" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2, whiteSpace: "nowrap",
          overflow: "hidden", textOverflow: "ellipsis" }}>{name}</Typography>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{team}</Typography>
      </Box>
      {platformStats ? (
        <Box sx={{ flexShrink: 0 }}>
          <PlatformSplitBar
            width={40}
            segments={platformStats.map(st => ({
              provider: st.provider,
              exposure: st.exposure,
              drafts: st.drafts,
              totalDrafts: st.totalDrafts,
            }))}
          />
        </Box>
      ) : (
        <Box sx={{ minWidth: 80, textAlign: "right" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: posColor }}>{exposure}%</Typography>
          <Box sx={{ height: 4, bgcolor: "action.hover", borderRadius: 1, mt: 0.5, overflow: "hidden" }}>
            <Box sx={{ width: `${expPct}%`, height: "100%", bgcolor: posColor, opacity: 0.7, borderRadius: 1 }} />
          </Box>
          <Typography sx={{ fontSize: 10, color: "text.secondary", mt: 0.25 }}>{count} picks</Typography>
        </Box>
      )}
    </>
  );
}

interface MobilePlayerSummaryProps {
  myAdp: string;
  udAdp: React.ReactNode;
  clv: React.ReactNode;
  clvPct: React.ReactNode;
  clvColor: string;
  adpLoading: boolean;
  marketLabel: string;
}

/** Mobile summary for a mixed portfolio: one line per platform, each scored on
 *  its own board. Replaces the blended tiles, whose "My ADP" would average a
 *  216-pick Underdog scale against a 240-pick DraftKings one. */
function MobilePlatformSummary({ platformStats, adpLoading }: { platformStats: PlayerPlatformStat[]; adpLoading: boolean }) {
  const fmt = (v: number | null) => (v === null || isNaN(v) ? "\u2014" : v.toFixed(1));
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pt: 1, width: "100%" }}>
      {platformStats.map(st => (
        <Box key={st.provider} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <PlatformChip provider={st.provider} size="sm" />
          {adpLoading ? (
            <Skeleton variant="text" width={140} />
          ) : (
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
              My ADP <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>{fmt(st.myAdp)}</Box>
              {"  ·  "}Mkt <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>{fmt(st.marketAdp)}</Box>
              {"  ·  "}CLV{" "}
              <Box component="span" sx={{ fontWeight: 700,
                color: st.clv === null ? "text.primary" : st.clv >= 0 ? "success.main" : "error.main" }}>
                {st.clv !== null && st.clv > 0 ? "+" : ""}{fmt(st.clv)}
              </Box>
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function MobilePlayerSummary({ myAdp, udAdp, clv, clvPct, clvColor, adpLoading, marketLabel }: MobilePlayerSummaryProps) {
  const clvNum = typeof clv === "string" ? parseFloat(clv) : null;
  const clvStr = typeof clv === "string" ? clv : "-";
  const clvPctStr = typeof clvPct === "string" ? clvPct : "-";
  const stats = [
    { label: "My ADP", value: myAdp },
    { label: marketLabel, value: adpLoading ? <Skeleton variant="text" width={36} /> : udAdp },
    {
      label: "CLV",
      value: adpLoading
        ? <Skeleton variant="text" width={36} />
        : <Box component="span" sx={{ color: clvColor, fontWeight: 600 }}>
            {clvNum !== null && !isNaN(clvNum) && clvNum > 0 ? "+" : ""}{clvStr}
          </Box>,
    },
    {
      label: "CLV %",
      value: adpLoading
        ? <Skeleton variant="text" width={32} />
        : <Box component="span" sx={{ color: clvColor, fontWeight: 600 }}>
            {parseFloat(clvPctStr) > 0 ? "+" : ""}{clvPctStr}%
          </Box>,
    },
  ];
  return (
    <>
      {stats.map(({ label, value }) => (
        <Box key={label} sx={{ pt: 1 }}>
          <Typography sx={{ fontSize: 10, color: "text.secondary", fontWeight: 500 }}>{label}</Typography>
          <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
        </Box>
      ))}
    </>
  );
}

export default function MyPlayersPage() {
  const {
    filteredRawData,
    teamOptions,
    teamFilter,
    setTeamFilter,
    rawData,
    combosCache,
    providerFilter,
    hasMultipleProviders,
    providersPresent,
  } = useDraftData();
  const { adpLoading, adpHistory, rookieNames, dkAdpTrend } = useAdp();
  const { lookupForProvider, labelForProvider } = useMarketAdp();
  const { visibleColumns, setVisibleColumns } = useUIState();

  // For per-player display columns we need ONE market. When a single platform is
  // selected, use it; when viewing both, use the platform the player was drafted
  // on most. CLV itself is always scored per-pick against each pick's own market.
  const marketForPlayer = useCallback(
    (rows: CsvRow[]) => {
      const prov = providerFilter !== "all" ? providerFilter : dominantProvider(rows);
      return { lookup: lookupForProvider(prov), prov };
    },
    [providerFilter, lookupForProvider]
  );
  // Column header for the market-ADP column: a single platform's label when one
  // is active/loaded, otherwise a neutral "Market ADP".
  const marketLabel =
    providerFilter !== "all" ? labelForProvider(providerFilter) :
    hasMultipleProviders ? "Market ADP" :
    labelForProvider(providersPresent[0]);

  // ── Page-local state ────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  // Split mode introduces per-platform sort keys ("clv:DraftKings"), so the sort
  // column is a plain string; the base ids remain the SortColumn union members.
  const [sortColumn, setSortColumn] = useState<string>("myAdp");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filterName, setFilterName] = useState(() => searchParams.get("q") ?? "");
  // Deep link: /players?q=<name> also auto-expands that player's row.
  const [pendingExpand, setPendingExpand] = useState<string | null>(() => searchParams.get("q"));

  // Clear the ?q param from the URL once we've consumed it
  useEffect(() => {
    if (searchParams.has("q")) {
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [adpRange, setAdpRange] = useState<[number, number]>(DEFAULT_ADP_RANGE);
  const [adpRangePending, setAdpRangePending] = useState<[number, number]>(DEFAULT_ADP_RANGE);
  const [expandedPlayer, setExpandedPlayer] = useState<string | false>(false);
  const [selectedPositions, setSelectedPositions] = useState<Set<string>>(new Set());
  const [rookieOnly, setRookieOnly] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const hasData = rawData.length > 0;

  const theme = useTheme();
  const sc = useStatusColors();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const darkMode = theme.palette.mode === "dark";
  const primaryColor = darkMode ? brand.primary.dark : brand.primary.light;

  // ── Back-to-top scroll listener ─────────────────────────────────
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const handler = () => setShowBackToTop(el.scrollTop > 500);
    el.addEventListener("scroll", handler);
    return () => el.removeEventListener("scroll", handler);
  }, []);

  // ── Derived data ────────────────────────────────────────────────
  const playersData = useMemo(() => groupDataByPlayer(filteredRawData), [filteredRawData]);

  const playersByDraftIndex = useMemo(
    () => buildPlayersByDraftIndex(playersData),
    [playersData]
  );

  // Market ADP for the pick-trend overlay, keyed by normalized name + position.
  // One series PER PLATFORM the player was drafted on — Underdog's dense dated
  // history, DraftKings' coarse 3-point trajectory — so the chart never has to
  // elect a single board for a mixed player. See helpers/marketSeries.ts.
  const marketSeriesByKey = useMemo(
    () => buildMarketSeriesByKey(playersData, adpHistory, dkAdpTrend, lookupForProvider),
    [playersData, adpHistory, dkAdpTrend, lookupForProvider]
  );

  const totalDrafts = useMemo(() => {
    if (!filteredRawData.length) return 0;
    return new Set(filteredRawData.map(row => row["Draft"])).size;
  }, [filteredRawData]);

  // ── Platform split ──────────────────────────────────────────────
  // Only when BOTH platforms are loaded and neither is filtered out. Scoping to
  // one platform, or loading one, keeps the single-column table it always was.
  const splitProviders = useMemo(
    () => (hasMultipleProviders && providerFilter === "all" ? sortPlatforms(providersPresent) : []),
    [hasMultipleProviders, providerFilter, providersPresent]
  );
  const splitMode = splitProviders.length > 1;

  // Per-platform draft counts — the exposure DENOMINATORS. Blending them was the
  // bug: 10-of-10 DraftKings drafts is 100% DK exposure, not 9% of a 110-draft
  // pool that is mostly Underdog.
  const draftTotals = useMemo(() => {
    const totals = draftTotalsByProvider(filteredRawData);
    // A platform whose drafts are all filtered out still gets a column, showing
    // an explicit 0 rather than silently disappearing mid-comparison.
    for (const p of providersPresent) totals[p] ??= 0;
    return totals;
  }, [filteredRawData, providersPresent]);

  const platformStatsByKey = useMemo(() => {
    const map = new Map<string, PlayerPlatformStat[]>();
    if (!splitMode) return map;
    for (const player of playersData) {
      map.set(
        `${player.name}|${player.team}|${player.position}`,
        playerPlatformStats(player, draftTotals, lookupForProvider)
      );
    }
    return map;
  }, [splitMode, playersData, draftTotals, lookupForProvider]);

  const playerTableData = useMemo(() => {
    return playersData.slice().sort((a, b) => {
      const adpA = a.pickNumbers.length
        ? a.pickNumbers.reduce((x, y) => x + y, 0) / a.pickNumbers.length
        : Infinity;
      const adpB = b.pickNumbers.length
        ? b.pickNumbers.reduce((x, y) => x + y, 0) / b.pickNumbers.length
        : Infinity;
      return adpA - adpB;
    });
  }, [playersData]);

  const filteredPlayers = useMemo(() => {
    return playerTableData.filter(p => {
      if (teamFilter && p.team !== teamFilter) return false;
      if (filterName && !p.name.toLowerCase().includes(filterName.toLowerCase()))
        return false;
      if (selectedPositions.size > 0 && !selectedPositions.has(p.position))
        return false;
      if (rookieOnly && !rookieNames.has(normalizePlayerName(p.name || "")))
        return false;
      const udKey = p.name ? normalizePlayerName(p.name) : "";
      const udAdpRaw = marketForPlayer(p.rows).lookup.get(udKey);
      const udAdp = typeof udAdpRaw === "number" ? udAdpRaw : null;
      if (udAdp !== null) {
        if (udAdp < adpRange[0]) return false;
        if (adpRange[1] < DEFAULT_ADP_RANGE[1] && udAdp > adpRange[1]) return false;
      }
      return true;
    });
  }, [playerTableData, teamFilter, filterName, adpRange, marketForPlayer, selectedPositions, rookieOnly, rookieNames]);

  // Consume the deep-link expand: once data is in, expand the exact-name match.
  useEffect(() => {
    if (!pendingExpand || !playerTableData.length) return;
    const target = normalizePlayerName(pendingExpand);
    const match = playerTableData.find(p => normalizePlayerName(p.name || "") === target);
    if (match) setExpandedPlayer(`${match.name}|${match.team}|${match.position}`);
    setPendingExpand(null);
  }, [pendingExpand, playerTableData]);

  // ── Pre-compute per-player values once (not in render loop) ───
  const playerComputedMap = useMemo(() => {
    const map = new Map<string, { myAdp: string; udAdp: string | number; clv: string; clvPct: string; exposure: string; count: number }>();
    for (const player of playerTableData) {
      const rawMyAdp = player.pickNumbers.length
        ? player.pickNumbers.reduce((x, y) => x + y, 0) / player.pickNumbers.length
        : NaN;
      const myAdp = isNaN(rawMyAdp) ? "-" : rawMyAdp.toFixed(2);
      // Market ADP column: the player's dominant-platform (or filtered) market.
      const lookup = marketForPlayer(player.rows).lookup;
      const udAdpRaw = lookup.get(normalizePlayerName(player.name || ""));
      const udAdp = typeof udAdpRaw === "number" ? udAdpRaw : "-";
      // CLV: each pick scored on its OWN platform's market, then averaged.
      const { avgClv } = computePlayerCLVMulti(player.name, player.rows, lookupForProvider);
      const clv = avgClv !== null ? avgClv.toFixed(2) : "-";
      const clvPct = (avgClv !== null && typeof udAdpRaw === "number" && udAdpRaw !== 0)
        ? ((avgClv / udAdpRaw) * 100).toFixed(1) : "-";
      const playerDrafts = new Set(player.rows.map(row => row["Draft"])).size;
      const exposure = totalDrafts > 0 ? ((playerDrafts / totalDrafts) * 100).toFixed(1) : "-";
      const count = player.rows.length;
      const key = `${player.name}|${player.team}|${player.position}`;
      map.set(key, { myAdp, udAdp, clv, clvPct, exposure, count });
    }
    return map;
  }, [playerTableData, marketForPlayer, lookupForProvider, totalDrafts]);

  // Portfolio-average exposure — the divergence center for the exposure bars.
  const avgExposure = useMemo(() => {
    const vals = [...playerComputedMap.values()]
      .map(v => parseFloat(v.exposure))
      .filter(v => Number.isFinite(v));
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : undefined;
  }, [playerComputedMap]);

  const sortedPlayers = useMemo(() => {
    // A split column sorts on ONE platform's value ("clv:DraftKings"), so the
    // header you clicked is the number the ordering actually reflects. Players
    // with no data on that platform sort last in both directions.
    const [splitMetric, splitProvider] = sortColumn.includes(":")
      ? (sortColumn.split(":") as [keyof PlayerPlatformStat, string])
      : [null, null];

    if (splitMetric && splitProvider) {
      const valueFor = (p: typeof filteredPlayers[number]): number | null => {
        const stats = platformStatsByKey.get(`${p.name}|${p.team}|${p.position}`);
        const stat = stats?.find(x => x.provider === splitProvider);
        const raw = stat ? stat[splitMetric] : null;
        return typeof raw === "number" && !isNaN(raw) ? raw : null;
      };
      return filteredPlayers.slice().sort((a, b) => {
        const av = valueFor(a);
        const bv = valueFor(b);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return sortDirection === "asc" ? av - bv : bv - av;
      });
    }

    return filteredPlayers.slice().sort((a, b) => {
      const aKey = `${a.name}|${a.team}|${a.position}`;
      const bKey = `${b.name}|${b.team}|${b.position}`;
      const aComputed = playerComputedMap.get(aKey) ?? { myAdp: "-", udAdp: "-", clv: "-", clvPct: "-", exposure: "-", count: 0 };
      const bComputed = playerComputedMap.get(bKey) ?? { myAdp: "-", udAdp: "-", clv: "-", clvPct: "-", exposure: "-", count: 0 };
      // Phase 5: support sorting on all columns including text ones
      if (sortColumn === "name" || sortColumn === "team" || sortColumn === "position") {
        const aStr = sortColumn === "name" ? a.name : sortColumn === "team" ? a.team : a.position;
        const bStr = sortColumn === "name" ? b.name : sortColumn === "team" ? b.team : b.position;
        return sortDirection === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      }
      type ComputedRow = typeof aComputed;
      const col = sortColumn as keyof ComputedRow;
      const aVal: string | number = aComputed[col] ?? "";
      const bVal: string | number = bComputed[col] ?? "";
      if (typeof aVal === "string" && isNaN(Number(aVal)) && typeof bVal === "string" && isNaN(Number(bVal))) {
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      const cmp = Number(aVal) - Number(bVal);
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [filteredPlayers, sortColumn, sortDirection, playerComputedMap, platformStatsByKey]);

  // ── Handlers ────────────────────────────────────────────────────
  const handleToggle = useCallback(
    (key: string) => setExpandedPlayer(prev => (prev === key ? false : key)),
    []
  );

  const handleSort = (columnId: string) => {
    if (sortColumn === columnId) {
      setSortDirection(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const handleMobileSortColumnChange = (nextCol: string) => {
    if (nextCol !== sortColumn) {
      setSortColumn(nextCol);
      setSortDirection("asc");
    }
  };

  const handleResetFilters = () => {
    setTeamFilter("");
    setSelectedPositions(new Set());
    setRookieOnly(false);
    setFilterName("");
    setAdpRange(DEFAULT_ADP_RANGE);
    setAdpRangePending(DEFAULT_ADP_RANGE);
  };

  const togglePosition = (pos: string) => {
    setSelectedPositions(prev => {
      const next = new Set(prev);
      if (next.has(pos)) next.delete(pos);
      else next.add(pos);
      return next;
    });
  };

  // Reset visible count when filters change
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filterName, selectedPositions, rookieOnly, adpRange, teamFilter, sortColumn, sortDirection]);

  const paginatedPlayers = useMemo(() => sortedPlayers.slice(0, visibleCount), [sortedPlayers, visibleCount]);

  const visibleCols = PLAYER_TABLE_COLUMNS.filter(col => visibleColumns.has(col.id));

  // All columns sortable (Phase 5). In split mode every MARKET-RELATIVE metric
  // becomes one sub-column per platform under a shared heading — those are the
  // numbers that differ by board. "# Drafted" stays single because it is a plain
  // count of picks, which is meaningful summed across platforms.
  const shellColumns: ColumnDef[] = visibleCols.flatMap((col): ColumnDef[] => {
    const baseLabel = col.id === "udAdp" ? "Market ADP" : col.label;
    if (!splitMode || !SPLIT_METRICS.has(col.id)) {
      return [{
        id: col.id,
        label: col.id === "udAdp" ? marketLabel : col.label,
        sortable: true,
        minWidth: col.minWidth,
        maxWidth: col.maxWidth,
        width: col.width,
      }];
    }
    return splitProviders.map(provider => ({
      id: `${col.id}:${provider}`,
      label: getPlatformShortLabel(provider),
      labelColor: getPlatformColor(provider, darkMode),
      group: baseLabel,
      sortable: true,
      width: col.id === "exposure" ? 78 : 64,
      align: "left" as const,
    }));
  });

  const sortState: SortState = { key: sortColumn, dir: sortDirection };

  const activeFilterCount = [
    teamFilter !== "",
    selectedPositions.size > 0,
    rookieOnly,
    filterName !== "",
    adpRange[0] !== DEFAULT_ADP_RANGE[0] || adpRange[1] !== DEFAULT_ADP_RANGE[1],
  ].filter(Boolean).length;

  // ── Empty state ─────────────────────────────────────────────────
  if (!hasData) {
    return <HeroEmptyState />;
  }

  return (
    <>
      {/* Shared filter bar */}
      <FilterBar
        filterName={filterName}
        onFilterName={setFilterName}
        positions={POSITIONS}
        selectedPositions={selectedPositions}
        onTogglePosition={togglePosition}
        rookieFilter={rookieOnly}
        onToggleRookie={() => setRookieOnly(v => !v)}
        teamOptions={teamOptions}
        teamFilter={teamFilter}
        onTeamFilter={setTeamFilter}
        adpRangePending={adpRangePending}
        onAdpRangeChange={(_, v) => setAdpRangePending(v as [number, number])}
        onAdpRangeCommit={(_, v) => setAdpRange(v as [number, number])}
        adpRangeMax={providerFilter === "DraftKings" ? 240 : 216}
        adpRangeLabel={marketLabel}
        activeFilterCount={activeFilterCount}
        onReset={handleResetFilters}
        resultText={isMobile ? undefined : `${sortedPlayers.length} of ${playersData.length} players`}
        actions={
          !isMobile ? (
            <ColumnToggle
              columns={PLAYER_TABLE_COLUMNS}
              visibleColumns={visibleColumns}
              onChange={setVisibleColumns}
            />
          ) : undefined
        }
      />

      {isMobile && (
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 0, flex: 1 }}>
            <Select
              value={sortColumn}
              onChange={e => handleMobileSortColumnChange(e.target.value as SortColumn)}
              displayEmpty
              inputProps={{ "aria-label": "Sort players by" }}
              sx={{ fontSize: 13 }}
            >
              {MOBILE_SORT_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                  Sort: {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSortDirection(d => (d === "asc" ? "desc" : "asc"))}
            sx={{ whiteSpace: "nowrap", minWidth: 92 }}
          >
            {sortDirection === "asc" ? "Asc ↑" : "Desc ↓"}
          </Button>
        </Box>
      )}

      {/* Mobile card view */}
      {isMobile ? (
        <Box sx={{ px: 2, py: 1.5 }}>
          {paginatedPlayers.map(player => {
            const key = `${player.name}|${player.team}|${player.position}`;
            const computed = playerComputedMap.get(key) ?? { myAdp: "-", udAdp: "-" as string | number, clv: "-", clvPct: "-", exposure: "-", count: 0 };
            const { myAdp, exposure, count } = computed;
            const udAdp = adpLoading ? "-" : String(computed.udAdp);
            const clv = adpLoading ? "-" : computed.clv;
            const clvPct = adpLoading ? "-" : computed.clvPct;
            const posColor = POSITION_COLORS[player.position] || "#888";
            const clvNum = parseFloat(clv);
            const clvColor = !isNaN(clvNum)
              ? clvNum > 0 ? sc.good : clvNum < 0 ? sc.bad : "text.primary"
              : "text.primary";
            return (
              <MobileCard
                key={key}
                accentColor={posColor}
                expanded={expandedPlayer === key}
                onToggle={() => handleToggle(key)}
                header={
                  <MobilePlayerHeader
                    name={player.name}
                    team={player.team}
                    position={player.position}
                    exposure={exposure}
                    count={count}
                    posColor={posColor}
                    platformStats={splitMode ? platformStatsByKey.get(key) : undefined}
                  />
                }
                summary={
                  splitMode && platformStatsByKey.get(key) ? (
                    <MobilePlatformSummary
                      platformStats={platformStatsByKey.get(key)!}
                      adpLoading={adpLoading}
                    />
                  ) : (
                    <MobilePlayerSummary
                      myAdp={myAdp}
                      udAdp={udAdp}
                      clv={clv}
                      clvPct={clvPct}
                      clvColor={clvColor}
                      adpLoading={adpLoading}
                      marketLabel={marketLabel}
                    />
                  )
                }
                expandedContent={
                  expandedPlayer === key ? (
                    <Box sx={{ mx: -2, overflowX: "auto" }}>
                      <Table size="small">
                        <TableBody>
                          <PlayerRow
                            player={player}
                            myAdp={myAdp}
                            udAdp={udAdp}
                            clv={clv}
                            clvPct={clvPct}
                            exposure={exposure}
                            count={count}
                            isOpen={true}
                            onClick={() => {}}
                            TEAM_COLORS={TEAM_COLORS}
                            POSITION_COLORS={POSITION_COLORS}
                            avgExposure={avgExposure}
                            allPlayers={playerTableData}
                            visibleColumns={new Set()}
                            playersByDraftIndex={playersByDraftIndex}
                            totalDrafts={totalDrafts}
                            rowIndex={0}
                            combosCache={combosCache}
                            marketSeries={marketSeriesByKey.get(`${normalizePlayerName(player.name)}|${player.position}`)}
                            splitProviders={splitMode ? splitProviders : undefined}
                            platformStats={platformStatsByKey.get(key)}
                            expandedOnly
                          />
                        </TableBody>
                      </Table>
                    </Box>
                  ) : null
                }
              />
            );
          })}
          {paginatedPlayers.length === 0 && (
            <EmptyState
              dense
              icon={<PeopleOutlinedIcon />}
              title="No players match your filters"
              hint="Try widening the ADP range or removing position filters."
            />
          )}
          {visibleCount < sortedPlayers.length && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                Show more ({sortedPlayers.length - visibleCount} remaining)
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <>
          {/* Desktop table shell */}
          <DataTableShell
            columns={shellColumns}
            hasLeadingColumn
            items={paginatedPlayers}
            totalCount={sortedPlayers.length}
            visibleCount={visibleCount}
            onShowMore={() => setVisibleCount(c => c + PAGE_SIZE)}
            sortState={sortState}
            onSort={handleSort}
            tableContainerRef={tableContainerRef}
            tableLabel="Players table"
            emptyState={
              <EmptyState
                dense
                icon={<PeopleOutlinedIcon />}
                title="No players match your filters"
                hint="Try widening the ADP range or removing position filters."
              />
            }
            renderRow={(player, rowIdx) => {
              const key = `${player.name}|${player.team}|${player.position}`;
              const computed = playerComputedMap.get(key) ?? { myAdp: "-", udAdp: "-" as string | number, clv: "-", clvPct: "-", exposure: "-", count: 0 };
              const { myAdp, exposure, count } = computed;
              const udAdp = adpLoading ? "-" : computed.udAdp;
              const clv = adpLoading ? "-" : computed.clv;
              const clvPct = adpLoading ? "-" : computed.clvPct;
              return (
                <PlayerRow
                  key={key}
                  player={player}
                  myAdp={myAdp}
                  udAdp={udAdp}
                  clv={clv}
                  clvPct={clvPct}
                  exposure={exposure}
                  count={count}
                  isOpen={expandedPlayer === key}
                  onClick={() => {}}
                  onToggle={handleToggle}
                  playerKey={key}
                  TEAM_COLORS={TEAM_COLORS}
                  POSITION_COLORS={POSITION_COLORS}
                  avgExposure={avgExposure}
                  allPlayers={playerTableData}
                  visibleColumns={visibleColumns}
                  playersByDraftIndex={playersByDraftIndex}
                  totalDrafts={totalDrafts}
                  rowIndex={rowIdx}
                  combosCache={combosCache}
                  marketSeries={marketSeriesByKey.get(`${normalizePlayerName(player.name)}|${player.position}`)}
                  splitProviders={splitMode ? splitProviders : undefined}
                  platformStats={platformStatsByKey.get(key)}
                />
              );
            }}
          />

          {/* Back to top FAB */}
          {showBackToTop && (
            <Fab
              size="small"
              aria-label="Back to top"
              onClick={() => tableContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
              sx={{
                position: "fixed",
                bottom: 24,
                right: 24,
                bgcolor: primaryColor,
                color: "#fff",
                "&:hover": { bgcolor: primaryColor, opacity: 0.9 },
                zIndex: 50,
              }}
            >
              <KeyboardArrowUpIcon />
            </Fab>
          )}
        </>
      )}
    </>
  );
}
